import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { toast } from "sonner";

const IMG = "https://images.pexels.com/photos/17314914/pexels-photo-17314914.jpeg";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.googleBlocked) {
      toast.error("No RTBA rep account found for that Google email. Register with your REP code first.");
    }
  }, [location.state]);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", rep_code: "" });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== confirmPassword) {
      setError("Passwords do not match. Please re-enter them.");
      return;
    }
    setBusy(true);
    try {
      await register({ ...form, rep_code: form.rep_code.trim().toUpperCase() });
      toast.success("Account created — check your email for a verification code.");
      navigate("/verify", { state: { email: form.email.toLowerCase() } });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#1A1A1A] text-white">
      <div className="flex items-center justify-center p-6 sm:p-12 grid-lines order-2 lg:order-1">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <Link to="/" className="flex items-center gap-2.5 mb-8">
            <img src="/rtb-logo.png?v=2" alt="Right To Bear" className="h-16 w-auto object-contain" />
            <span className="flex flex-col leading-none">
              <span className="font-display text-2xl tracking-tight">RIGHT TO BEAR</span>
              <span className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--rtb-copper))] font-medium mt-1">
                Pure Peace of Mind
              </span>
            </span>
          </Link>

          <div className="overline text-[hsl(var(--primary))] mb-3">Become A Rep</div>
          <h1 className="font-display text-5xl tracking-tight leading-none mb-8">JOIN THE TEAM</h1>

          {error && (
            <div data-testid="register-error" className="border border-[hsl(var(--primary))] text-[hsl(var(--primary))] text-sm px-4 py-3 mb-5">
              {error}
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="overline text-white/50 block mb-2">
                RTBA REP Code <span className="text-[hsl(var(--primary))]">*</span>
              </label>
              <input
                data-testid="register-repcode"
                required
                value={form.rep_code}
                onChange={set("rep_code")}
                className="w-full bg-[#22303C] border border-[hsl(var(--primary))]/40 px-4 py-3 text-white tracking-[0.2em] uppercase focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                placeholder="e.g. RTBJS03"
              />
              <p className="text-white/40 text-xs mt-1.5">
                The code assigned to you by the Right To Bear Association. Required to prove you're a legit rep.
              </p>
            </div>
            <div>
              <label className="overline text-white/50 block mb-2">Full Name</label>
              <input
                data-testid="register-name"
                required
                value={form.name}
                onChange={set("name")}
                className="w-full bg-[#22303C] border border-white/15 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                placeholder="Jane Rep"
              />
            </div>
            <div>
              <label className="overline text-white/50 block mb-2">Email</label>
              <input
                data-testid="register-email"
                type="email"
                required
                value={form.email}
                onChange={set("email")}
                className="w-full bg-[#22303C] border border-white/15 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                placeholder="rep@righttobear.com"
              />
            </div>
            <div>
              <label className="overline text-white/50 block mb-2">
                Mobile <span className="text-white/30">(for text reminders)</span>
              </label>
              <input
                data-testid="register-phone"
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                className="w-full bg-[#22303C] border border-white/15 px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                placeholder="+1 512 555 0134"
              />
            </div>
            <div>
              <label className="overline text-white/50 block mb-2">Password</label>
              <div className="relative">
                <input
                  data-testid="register-password"
                  type={showPassword ? "text" : "password"}
                  required
                  minLength={6}
                  value={form.password}
                  onChange={set("password")}
                  className="w-full bg-[#22303C] border border-white/15 px-4 py-3 pr-12 text-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                  placeholder="Min 6 characters"
                />
                <button
                  type="button"
                  data-testid="toggle-password"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <label className="overline text-white/50 block mb-2">Confirm Password</label>
              <div className="relative">
                <input
                  data-testid="register-confirm-password"
                  type={showPassword ? "text" : "password"}
                  required
                  minLength={6}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full bg-[#22303C] border border-white/15 px-4 py-3 pr-12 text-white focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                  placeholder="Re-enter your password"
                />
                <button
                  type="button"
                  data-testid="toggle-confirm-password"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {confirmPassword && form.password !== confirmPassword && (
                <p data-testid="password-mismatch" className="text-[hsl(var(--primary))] text-xs mt-1.5">
                  Passwords don't match yet.
                </p>
              )}
            </div>
            <button
              data-testid="register-submit"
              type="submit"
              disabled={busy}
              className="w-full flex items-center justify-center gap-2 bg-[hsl(var(--primary))] text-white overline py-4 hard-shadow-hover disabled:opacity-50"
            >
              {busy ? "Enlisting…" : "Create Account"} <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          <p className="text-white/40 text-sm mt-8">
            Already a rep?{" "}
            <Link to="/login" data-testid="to-login" className="text-white underline hover:text-[hsl(var(--primary))]">
              Log in
            </Link>
          </p>
        </motion.div>
      </div>

      <div className="relative hidden lg:block overflow-hidden border-l border-white/10 order-1 lg:order-2">
        <img src={IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-[#1A1A1A]/30 to-transparent" />
        <div className="absolute bottom-10 right-10 text-right">
          <div className="font-display text-7xl leading-[0.85] tracking-tight">
            REPRESENT
            <br />
            THE <span className="text-[hsl(var(--primary))]">CAUSE.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
